import { Button, UseStepContext } from '@consigli/facade';
import clsx from 'clsx';
import { FC, lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { WizardFormData } from '../wizard-context';

const MapView = lazy(() => import('../map-view'));

const MAP_HEIGHT = 'h-[calc(95vh-280px)]';

const ClientOnlyMapView: FC<{
  label?: string;
  lat: number | null;
  lon: number | null;
  onMapClick: (lat: number, lon: number) => void;
  showMarker: boolean;
  zoom: number;
  center?: [number, number];
}> = ({ lat, lon, ...otherProps }) => {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted) {
    return <div className={clsx(MAP_HEIGHT, 'w-full bg-gray-100 rounded-lg animate-pulse')} />;
  }

  return (
    <Suspense
      fallback={<div className={clsx(MAP_HEIGHT, 'w-full bg-gray-100 rounded-lg animate-pulse')} />}
    >
      <MapView lat={lat ?? undefined} lon={lon ?? undefined} height={MAP_HEIGHT} {...otherProps} />
    </Suspense>
  );
};

interface LocationStepProps {
  context: UseStepContext;
}

export const LocationStep: FC<LocationStepProps> = ({ context }) => {
  const { t } = useTranslation();
  const { next, previous, hasNext, hasPrevious } = context();
  const { setValue, watch } = useFormContext<WizardFormData>();

  // Watch form values for initial state
  const selectedSiteLat = watch('selectedSiteLat');
  const selectedSiteLon = watch('selectedSiteLon');

  // Local state to prevent re-renders on every map click
  const [localLat, setLocalLat] = useState<number | null>(selectedSiteLat);
  const [localLon, setLocalLon] = useState<number | null>(selectedSiteLon);

  const handleMapClick = useCallback((lat: number, lon: number) => {
    setLocalLat(lat);
    setLocalLon(lon);
  }, []);

  const handleNext = useCallback(() => {
    // Update form data and navigate to next step
    setValue('selectedSiteLat', localLat);
    setValue('selectedSiteLon', localLon);
    next();
  }, [localLat, localLon, setValue, next]);

  const hasLocation = localLat !== null && localLon !== null;

  return (
    <div className="w-full mt-4">
      <ClientOnlyMapView
        label={t('package-creation.select-site')}
        lat={localLat}
        lon={localLon}
        onMapClick={handleMapClick}
        showMarker={hasLocation}
        zoom={hasLocation ? 15 : 10}
        center={hasLocation ? [localLat!, localLon!] : undefined}
      />
      <div className="flex items-stretch gap-2 mt-6">
        {hasPrevious && (
          <Button secondary rounded onClick={previous} type="button">
            {t('package-creation.back')}
          </Button>
        )}
        {hasNext && (
          <Button primary rounded onClick={handleNext} type="button" disabled={!hasLocation}>
            {t('package-creation.next')}
          </Button>
        )}
      </div>
    </div>
  );
};
